import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  ListGroup,
  ListGroupItem,
  Badge,
  Button,
} from 'reactstrap';
import { FirebaseContext } from '../../components/Firebase';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore, OrganizationMembership, getCategoryLabel } from '../../store/organization.store';
import { useCollectionCache } from '../../services/collectionCache.service';
import { AuthUserContext } from '../../components/Session';
import { useToast } from '../../contexts/ToastContext';
import * as ROUTES from '../../constants/routes';

const SelectOrganization: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext) as any;
  const auth = useAuthStore(state => state.auth);

  // Check if we came from sign-in (or have no history to go back to)
  // We use location.state to pass this info, or check history length
  const [canGoBack] = useState(() => {
    // If navigated from sign-in, location.state will have fromSignIn flag
    const state = location.state as { fromSignIn?: boolean } | undefined;
    if (state?.fromSignIn) return false;

    // If history length is <= 2, we likely came from sign-in or direct navigation
    // (1 = direct load, 2 = sign-in -> this page)
    return history.length > 2;
  });

  const storeOrganizations = useOrganizationStore(state => state.organizations);
  const isLoading = useOrganizationStore(state => state.isLoading);
  const setCurrentOrganization = useOrganizationStore(state => state.setCurrentOrganization);
  const clearOrganization = useOrganizationStore(state => state.clearOrganization);
  const { showSuccess } = useToast();

  // Collection cache for loading org-specific collections
  const loadOrgCollections = useCollectionCache(state => state.loadOrgCollections);

  // Use store organizations as primary (updated when user joins new org via invite)
  // Fall back to authUser.organizations for initial load before store is populated
  const organizations: OrganizationMembership[] = useMemo(() => {
    return storeOrganizations.length > 0
      ? storeOrganizations
      : (authUser?.organizations || []);
  }, [storeOrganizations, authUser?.organizations]);

  // Track if we have orgs from authUser (which loads before store finishes)
  const hasOrgsFromAuthUser = authUser?.organizations?.length > 0;

  // Redirect to get-started if no organizations (but wait for data to be available)
  useEffect(() => {
    // Wait for either authUser orgs or store loading to complete
    if (!hasOrgsFromAuthUser && isLoading) return;

    if (organizations.length === 0) {
      history.push(ROUTES.GET_STARTED);
    }
  }, [organizations, history, isLoading, hasOrgsFromAuthUser]);

  // Handle selecting an organization
  const handleSelectOrganization = async (org: OrganizationMembership) => {
    if (!firebase || !auth) return;

    // Set the current organization in the store (include org type and category if available)
    setCurrentOrganization(org.orgId, org.orgName, org.role, org.orgType || 'artist', org.orgCategory);

    // Load org-specific collections (clears old org data and loads new org's collections)
    // This is important for chatbot to use correct org data for matching
    loadOrgCollections(firebase, org.orgId);

    // Persist the selection to the user's profile
    try {
      await firebase.setUserCurrentOrg(auth.uid, org.orgId);
    } catch (err) {
      console.error('Failed to persist org selection:', err);
    }

    showSuccess(`Switched to "${org.orgName}"`);

    // Redirect to the library
    history.push(ROUTES.FIND_AUDIO);
  };

  // Handle going back
  const handleGoBack = () => {
    history.goBack();
  };

  // Handle sign out
  const handleSignOut = async () => {
    if (!firebase) return;

    clearOrganization();
    await firebase.doSignOut();
    history.push(ROUTES.SIGN_IN);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'danger';
      case 'OPERATOR': return 'warning';
      default: return 'info';
    }
  };

  // Show nothing while waiting for data - but can render as soon as authUser orgs are available
  if (!hasOrgsFromAuthUser && isLoading) {
    return null;
  }

  // Will redirect to get-started if no orgs
  if (organizations.length === 0) {
    return null;
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card>
            <CardBody>
              <CardTitle tag="h3" className="text-white text-center mb-4">
                Select Organization
              </CardTitle>

              <p className="text-muted text-center mb-4">
                Choose which organization you want to access.
              </p>

              <ListGroup flush className="mb-4">
                {organizations.map((org) => (
                  <ListGroupItem
                    key={org.orgId}
                    className="bg-transparent border-secondary d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSelectOrganization(org)}
                    tag="button"
                    action
                  >
                    <div>
                      <span className="text-white fw-bold">{org.orgName}</span>
                      <Badge
                        color={org.orgType === 'artist' ? 'primary' : 'secondary'}
                        className="ms-2"
                        style={{ fontSize: '0.7rem', opacity: 0.8 }}
                      >
                        {getCategoryLabel(org.orgCategory, org.orgType)}
                      </Badge>
                    </div>
                    <Badge color={getRoleBadgeColor(org.role)}>
                      {org.role}
                    </Badge>
                  </ListGroupItem>
                ))}
              </ListGroup>

              <hr className="my-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              <div className="d-flex justify-content-between">
                {canGoBack ? (
                  <Button color="link" className="text-muted p-0" onClick={handleGoBack}>
                    <i className="tim-icons icon-minimal-left" style={{ marginRight: '4px' }} />
                    Back
                  </Button>
                ) : (
                  <Button color="link" className="text-muted p-0" onClick={handleSignOut}>
                    Sign Out
                  </Button>
                )}
                <Button
                  color="link"
                  className="text-muted p-0"
                  onClick={() => history.push(ROUTES.GET_STARTED)}
                >
                  Create or Join Organization
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SelectOrganization;
